import maplibregl, { type Map as MlMap } from "maplibre-gl";
import { ambientEnabled } from "./ambient-flow";

/**
 * v0.9.5c — the user's point on the map: a 📍 teardrop pin (#00d4ff, white outline, ~28 px,
 * its TIP on the exact coordinate — anchor "bottom") with ONE slow cyan ripple at the tip
 * (2.4 s, ≤32 px) — clearly unlike the dam ripples (orange/red, several rings).
 * A DOM marker, so it sits above every map layer (staffs, dams, gauges) at every zoom.
 * The ripple is stepped from the route animation's existing requestAnimationFrame
 * (route-layer.ts → tickUserPin), honours "แสดงน้ำไหล" and prefers-reduced-motion.
 * Tap → "คุณอยู่ที่นี่" (GPS) / "จุดที่เลือก" (search / tap) + the place name.
 *
 * (Replaces the old start-pin, whose wrapper had `position: relative` overriding MapLibre's
 *  absolute marker positioning → a full-width box, so the dot always sat at the screen's
 *  left edge on the right latitude.)
 */
export type PinKind = "gps" | "picked";

const PIN_SVG = `<svg viewBox="0 0 28 36" width="28" height="36" aria-hidden="true">
  <path d="M14 35C14 35 3 21.5 3 13.5a11 11 0 0 1 22 0C25 21.5 14 35 14 35z" fill="#00d4ff" stroke="#fff" stroke-width="2.4" stroke-linejoin="round"/>
  <circle cx="14" cy="13.5" r="4.2" fill="#fff"/></svg>`;
const CYCLE_MS = 2400, MAX_R = 32;

let marker: maplibregl.Marker | null = null;
let ripple: HTMLElement | null = null;
let tip: HTMLElement | null = null;
let kind: PinKind = "picked";
let place = "";
let elev: string | null = null; // "สูงจากระดับน้ำทะเลประมาณ N ม." (v0.9.10 B)
let near: string | null = null; // "ใกล้<แม่น้ำ> X กม." — 2nd line (v0.9.5c decision 2)
const reduced = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

let lastAt = "";
export function setUserPin(map: MlMap, at: { lon: number; lat: number }, k: PinKind): void {
  kind = k;
  // a NEW point: forget the previous point's place name / height (they arrive again for this one —
  // before, a failed or slow place lookup left the old ต./อ. on the new pin)
  const key = `${at.lon.toFixed(5)},${at.lat.toFixed(5)}`;
  if (key !== lastAt) { lastAt = key; place = ""; elev = null; }
  if (!marker) {
    const el = document.createElement("div");
    el.className = "user-pin";
    el.setAttribute("role", "button");
    el.setAttribute("aria-label", "ตำแหน่งของคุณ");
    ripple = document.createElement("span"); ripple.className = "user-pin-ripple";
    const body = document.createElement("span"); body.className = "user-pin-body"; body.innerHTML = PIN_SVG;
    tip = document.createElement("span"); tip.className = "user-pin-tip"; tip.hidden = true;
    el.append(ripple, body, tip);
    // a tap on the pin shows its label; it must not also "tap the map" (which would re-analyse)
    for (const ev of ["mousedown", "touchstart", "click", "dblclick"]) el.addEventListener(ev, (e) => e.stopPropagation());
    el.addEventListener("click", () => { if (!tip) return; tip.textContent = label(); tip.hidden = !tip.hidden; placeTip(); });
    marker = new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat([at.lon, at.lat]).addTo(map);
  } else {
    const ll = marker.getLngLat();
    if (ll.lng !== at.lon || ll.lat !== at.lat) { marker.setLngLat([at.lon, at.lat]); if (tip) tip.hidden = true; }
  }
  if (tip && !tip.hidden) tip.textContent = label();
  tickUserPin(performance.now());
}
export function setUserPinPlace(text: string): void { place = text; if (tip && !tip.hidden) tip.textContent = label(); }
export function clearUserPin(): void { marker?.remove(); marker = null; ripple = null; tip = null; place = ""; near = null; elev = null; lastAt = ""; }
export function userPinLngLat(): [number, number] | null { const l = marker?.getLngLat(); return l ? [l.lng, l.lat] : null; }

/** The label sits above the pin; if that would run under the 〰 chip / search box / top of the
 *  screen, it flips below the pin instead. */
function placeTip(): void {
  if (!tip || tip.hidden) return;
  tip.classList.remove("below");
  const top = tip.getBoundingClientRect().top;
  let limit = 0;
  for (const q of ["#river-chip", ".search-box", ".pr-days", ".rf-chips"]) {
    const e = document.querySelector<HTMLElement>(q);
    if (e && !e.hidden && getComputedStyle(e).display !== "none") limit = Math.max(limit, e.getBoundingClientRect().bottom);
  }
  if (top < limit + 4) tip.classList.add("below");
}

function label(): string { return `${kind === "gps" ? "คุณอยู่ที่นี่" : "จุดที่เลือก"}${place ? ` · ${place}` : ""}${near ? `\n${near}` : ""}${elev ? `\n${elev}` : ""}`; }
export function setUserPinElev(text: string | null): void { elev = text; if (tip && !tip.hidden) tip.textContent = label(); }
export function setUserPinNear(text: string | null): void { near = text; if (tip && !tip.hidden) tip.textContent = label(); }

/** One ripple step — called from the route animation's rAF (no rAF loop of its own). */
export function tickUserPin(ts: number): void {
  if (!ripple) return;
  if (reduced() || !ambientEnabled()) { ripple.style.opacity = "0"; return; }
  const t = (ts % CYCLE_MS) / CYCLE_MS;                 // 0 → 1, slow
  const r = 3 + (MAX_R - 3) * (1 - (1 - t) * (1 - t));  // ease-out growth to ≤32 px
  ripple.style.width = ripple.style.height = `${(r * 2).toFixed(1)}px`;
  ripple.style.opacity = (0.85 * (1 - t)).toFixed(3);   // fades out
}
